// FIX-CCS-OUTBOX (spec §7.4, plan Stage D) — the outbox limits, as pure functions.
//
// R1  age:   an item more than 7 days past its capture time is not sent; the farmer is told, then it is removed.
//            "Capture time" here is the file time when it is within 10 minutes of the pick (a camera shot), else the
//            pick time (a gallery pick — R13 counts it on arrival anyway; an old gallery file must not expire on save).
// R2  size:  50 items or 300 MB, whichever comes first, counted over the whole phone's queue (every owner shares
//            the same browser storage).
//     space: before saving, navigator.storage.estimate() must leave room for the file (plus its preview and a margin).
// R7  bytes: the same SHA-256 already queued is refused ("Already saved").
// Timeout:   each send gets 30 s + 1 s per 16,000 bytes (a 128 kbit/s floor). A 10 MB photo gets 686 s; at 256 kbit/s
//            it needs ~328 s, so it finishes inside its limit on the plan's weak-signal test. Capped by the 20 MB file
//            limit (A1) at 1,341 s. A timeout is a network failure (back-off), never a hang.
// Back-off:  15 s, 30 s, 60 s … doubling per consecutive network failure, capped at 10 minutes.

import type { OutboxItem, OutboxSummary } from './types';
import { OUTBOX_TEST_BREAKS_ON, testBreak } from './testBreaks';

export const OUTBOX_MAX_ITEMS = 50;
export const OUTBOX_MAX_BYTES = 300 * 1024 * 1024;
export const OUTBOX_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const GALLERY_GAP_MS = 10 * 60 * 1000;
/** Free space kept beyond the file itself (preview, IndexedDB overhead, the rest of the app). */
export const OUTBOX_SPACE_MARGIN_BYTES = 20 * 1024 * 1024;

export const SEND_TIMEOUT_BASE_MS = 30_000;
export const SEND_TIMEOUT_BYTES_PER_S = 16_000;
export const BACKOFF_BASE_MS = 15_000;
export const BACKOFF_MAX_MS = 10 * 60 * 1000;

/** The send time limit for a file of this size. */
export function sendTimeoutMs(bytes: number): number {
  const b = Number.isFinite(bytes) && bytes > 0 ? bytes : 0;
  return SEND_TIMEOUT_BASE_MS + Math.ceil(b / SEND_TIMEOUT_BYTES_PER_S) * 1000;
}

/** Wait before the next attempt after `failures` consecutive network failures (1 → 15 s). */
export function backoffMs(failures: number): number {
  const n = Math.max(1, Math.floor(failures));
  return Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** (n - 1));
}

/** The capture time R1 is measured from (see header). */
export function captureTimeMs(item: Pick<OutboxItem, 'claims'>): number {
  const picked = Date.parse(item.claims.picked_at_client);
  const file = item.claims.captured_at_client ? Date.parse(item.claims.captured_at_client) : NaN;
  if (Number.isFinite(file) && Number.isFinite(picked) && Math.abs(picked - file) <= GALLERY_GAP_MS) return file;
  return picked;
}

export function isExpired(item: Pick<OutboxItem, 'claims'>, nowMs: number = Date.now()): boolean {
  const t = captureTimeMs(item);
  return Number.isFinite(t) && nowMs - t > OUTBOX_MAX_AGE_MS;
}

export type QueueRefusal = 'queue_full' | 'no_space' | 'already_saved';

export interface SpaceEstimate {
  usage?: number;
  quota?: number;
}

/** May a new file of `bytes` with hash `sha256` be queued? Null = yes. */
export function checkCanQueue(
  items: Pick<OutboxItem, 'sha256' | 'size_bytes'>[],
  bytes: number,
  sha256: string,
  estimate: SpaceEstimate | null,
): QueueRefusal | null {
  if (!(OUTBOX_TEST_BREAKS_ON && testBreak('dup')) && items.some((i) => i.sha256 === sha256)) return 'already_saved';
  const totalBytes = items.reduce((s, i) => s + (i.size_bytes || 0), 0);
  if (items.length >= OUTBOX_MAX_ITEMS || totalBytes + bytes > OUTBOX_MAX_BYTES) return 'queue_full';
  if (estimate && typeof estimate.quota === 'number' && typeof estimate.usage === 'number') {
    if (estimate.quota - estimate.usage < bytes * 2 + OUTBOX_SPACE_MARGIN_BYTES) return 'no_space';
  }
  return null;
}

/** Is the queue already full (the camera blocks before a new capture)? */
export function isQueueFull(items: Pick<OutboxItem, 'size_bytes'>[]): boolean {
  const totalBytes = items.reduce((s, i) => s + (i.size_bytes || 0), 0);
  return items.length >= OUTBOX_MAX_ITEMS || totalBytes >= OUTBOX_MAX_BYTES;
}

/** The signed-in farmer's view of the store (owner binding: other owners are a count only). */
export function summarise(items: OutboxItem[], userId: string | null): OutboxSummary {
  const mine = userId ? items.filter((i) => i.owner_user_id === userId) : [];
  return {
    pending: mine.filter((i) => i.state === 'pending' || i.state === 'sending').length,
    failed: mine.filter((i) => i.state === 'failed').length,
    otherAccounts: items.filter((i) => i.owner_user_id !== userId && i.state !== 'expired').length,
    bytes: mine.reduce((s, i) => s + (i.size_bytes || 0), 0),
    totalItems: items.length,
    totalBytes: items.reduce((s, i) => s + (i.size_bytes || 0), 0),
  };
}

/** A Blob's bytes (Blob.arrayBuffer where present; FileReader on older WebViews). */
export function blobBytes(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as ArrayBuffer);
    r.onerror = () => reject(r.error);
    r.readAsArrayBuffer(blob);
  });
}

/** SHA-256 of a Blob, lower-case hex. */
export async function sha256Hex(blob: Blob): Promise<string> {
  const buf = await blobBytes(blob);
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}
